import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiMessageCircle, FiMessageSquare, FiVolume2, FiSmile, FiFlag } = FiIcons;

const EMOJIS = ['👍', '❤️', '😂', '👏', '🔥'];

export default function MessageList({
  messages,
  reactions = [],
  currentUser,
  mode,
  recipient,
  onReact,
  onReport
}) {
  const scrollRef = useRef(null);
  const [activeReactionMsg, setActiveReactionMsg] = useState(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, reactions]);

  const toggleReactionMenu = (msgId) => {
    setActiveReactionMsg((prev) => (prev === msgId ? null : msgId));
  };

  const handleReact = (msgId, emoji) => {
    if (currentUser) {
      onReact(msgId, emoji);
    }
    setActiveReactionMsg(null);
  };

  if (mode === 'private' && !recipient) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center p-4 text-center bg-gray-50 dark:bg-[#0a0a0a] transition-colors">
        <div className="mb-3 rounded-full bg-red-600/10 p-3 text-red-600 dark:text-red-500 sm:mb-4 sm:p-4">
          <SafeIcon icon={FiMessageCircle} size={24} />
        </div>
        <p className="text-[11px] font-bold text-gray-800 dark:text-gray-300 sm:text-sm">
          Selecciona un contacto
        </p>
        <p className="mt-1 max-w-[200px] text-[10px] leading-snug text-gray-500 dark:text-gray-600 sm:text-xs">
          Elige a alguien de la lista superior para iniciar un chat privado.
        </p>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="scrollbar-hide flex-1 space-y-2.5 overflow-y-auto p-2 sm:space-y-4 sm:p-4 bg-gray-50 dark:bg-[#0a0a0a] transition-colors pb-12"
    >
      {messages.length === 0 && (
        <div className="flex h-full flex-col items-center justify-center p-4 text-center text-gray-400 dark:text-gray-600">
          <SafeIcon icon={FiMessageSquare} size={28} className="mb-2 opacity-35 sm:mb-3 sm:size-8" />
          <p className="text-[11px] italic font-medium sm:text-sm">
            {mode === 'private' ? `Inicia la conversación con ${recipient}.` : 'No se encontraron mensajes.'}
          </p>
        </div>
      )}

      {messages.map((message) => {
        const own = message.nickname === currentUser;
        const isAnnouncement = message.nickname.includes('ANUNCIO') || message.nickname.includes('RECORDATORIO');
        const msgReactions = reactions.filter((r) => r.message_id === message.id);
        const reactionCounts = msgReactions.reduce((acc, r) => {
          acc[r.emoji] = (acc[r.emoji] || 0) + 1;
          return acc;
        }, {});
        const hasReactions = Object.keys(reactionCounts).length > 0;
        const showMenu = activeReactionMsg === message.id;

        if (isAnnouncement) {
          return (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="my-3 flex flex-col items-center justify-center w-full px-2"
            >
              <div className="rounded-2xl border border-red-500/20 bg-red-500/5 dark:bg-red-950/20 px-4 py-3 text-center shadow-md max-w-[95%]">
                <p className="flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-500 sm:text-xs">
                  <SafeIcon icon={FiVolume2} size={14} /> {message.nickname}
                </p>
                <p className="mt-1 text-xs text-gray-800 dark:text-gray-200 sm:text-sm leading-relaxed font-semibold">
                  {message.content}
                </p>
              </div>
            </motion.div>
          );
        }

        return (
          <motion.div
            key={message.id}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex flex-col relative ${own ? 'items-end' : 'items-start'}`}
          >
            <div className="flex items-center gap-1.5 mb-0.5 px-1 sm:px-2">
              <span className="text-[8px] font-bold text-gray-400 dark:text-gray-500 sm:text-[10px]">
                {own ? 'Tú' : message.nickname}
              </span>
              {!own && onReport && (
                <button
                  type="button"
                  onClick={() => onReport(message)}
                  title="Reportar mensaje a moderadores"
                  className="text-gray-400 dark:text-gray-600 hover:text-red-500 transition-colors p-0.5"
                >
                  <SafeIcon icon={FiFlag} size={9} />
                </button>
              )}
            </div>

            <div className="relative max-w-[85%] group">
              <div
                onClick={() => toggleReactionMenu(message.id)}
                className={`break-words rounded-2xl px-3 py-2 text-[11px] shadow-sm sm:px-4 sm:py-2.5 sm:text-sm cursor-pointer transition-transform active:scale-[0.98] ${
                  own
                    ? 'rounded-tr-none bg-red-600 text-white'
                    : 'rounded-tl-none bg-white dark:bg-white/10 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-transparent'
                }`}
              >
                {message.content}
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleReactionMenu(message.id);
                }}
                className={`absolute top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity ${
                  own ? '-left-8' : '-right-8'
                }`}
                title="Añadir reacción"
              >
                <SafeIcon icon={FiSmile} size={12} className="text-gray-500 dark:text-gray-400" />
              </button>

              <AnimatePresence>
                {showMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, y: 5 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.8, y: 5 }}
                    className={`absolute z-10 flex items-center gap-1 p-1.5 rounded-full bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 -bottom-10 ${
                      own ? 'right-0' : 'left-0'
                    }`}
                  >
                    {EMOJIS.map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => handleReact(message.id, emoji)}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-lg transition-transform hover:scale-125"
                      >
                        {emoji}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {hasReactions && (
              <div className={`flex flex-wrap gap-1 mt-1 ${own ? 'justify-end' : 'justify-start'}`}>
                {Object.entries(reactionCounts).map(([emoji, count]) => (
                  <motion.button
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    key={emoji}
                    onClick={() => handleReact(message.id, emoji)}
                    className={`flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] sm:text-[10px] font-bold border transition-colors ${
                      msgReactions.some((r) => r.nickname === currentUser && r.emoji === emoji)
                        ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400'
                        : 'bg-white dark:bg-black/40 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <span>{emoji}</span>
                    <span>{count}</span>
                  </motion.button>
                ))}
              </div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}